<template>
  <div class="pos-manager-wrapper" v-if="showPosManagerButton">
    <!-- 触发按钮 -->
    <ElButton class="pos_manager_btn" @click="drawerVisible = true">
      POS机管理
    </ElButton>

    <!-- Drawer 抽屉 -->
    <ElDrawer
      v-model="drawerVisible"
      title="POS机管理"
      :size="920"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="pos-manager-content">
        <!-- 状态栏 -->
        <div class="status-bar">
          <div class="status-item">
            <span class="status-label">连接状态：</span>
            <ElTag :type="connectionStatusType" size="large">
              {{ connectionStatusText }}
            </ElTag>
          </div>
          <div class="status-item">
            <span class="status-label">健康状态：</span>
            <ElTag :type="healthStatusType" size="large">
              {{ healthStatusText }}
            </ElTag>
          </div>
        </div>

        <!-- 操作按钮区 -->
        <div class="action-bar">
          <ElButton
            v-if="
              serialConnectionStatus === PosConnectionStatus.DISCONNECTED ||
              serialConnectionStatus === PosConnectionStatus.ERROR
            "
            type="primary"
            @click="handleConnect"
            :loading="connecting"
          >
            连接POS机
          </ElButton>
          <ElButton
            v-else
            type="danger"
            @click="handleDisconnect"
            :loading="disconnecting"
          >
            断开连接
          </ElButton>
          <ElButton
            type="warning"
            @click="handleClearConnection"
            :disabled="
              serialConnectionStatus === PosConnectionStatus.CONNECTING
            "
          >
            清理连接
          </ElButton>
          <ElButton
            type="primary"
            @click="handleSettle"
            style="margin-left: auto"
            :loading="settleLoading"
          >
            清机结算
          </ElButton>
        </div>

        <!-- 交易记录表格 -->
        <div class="transaction-table">
          <ElTable
            :data="sortedTransactionRecords"
            border
            stripe
            max-height="500"
            style="width: 100%"
            v-loading="loading"
          >
            <ElTableColumn type="index" label="序号" width="60" align="center">
              <template #default="scope">
                {{ scope.$index + 1 }}
              </template>
            </ElTableColumn>

            <ElTableColumn prop="txnId" label="交易ID" width="150">
              <template #default="scope">
                <span class="txn-id">{{ scope.row.txnId || "-" }}</span>
              </template>
            </ElTableColumn>

            <ElTableColumn
              prop="eventName"
              label="交易类型"
              width="80"
              align="center"
            >
              <template #default="scope">
                <ElTag size="small">{{ scope.row.eventName }}</ElTag>
              </template>
            </ElTableColumn>

            <ElTableColumn prop="amount" label="金额" width="100" align="right">
              <template #default="scope">
                <span v-if="scope.row.amount">
                  {{ scope.row.amount }}
                </span>
                <span v-else>-</span>
              </template>
            </ElTableColumn>

            <ElTableColumn
              prop="timestamp"
              label="交易时间"
              width="140"
              align="right"
            >
              <template #default="scope">
                {{ formatTime(scope.row.timestamp) }}
              </template>
            </ElTableColumn>

            <ElTableColumn
              prop="requestStatus"
              label="请求状态"
              width="80"
              align="center"
            >
              <template #default="scope">
                <ElTag :type="getRequestStatusType(scope.row.requestStatus)">
                  {{ getRequestStatusText(scope.row.requestStatus) }}
                </ElTag>
              </template>
            </ElTableColumn>

            <ElTableColumn
              prop="status"
              label="状态码"
              width="60"
              align="center"
            >
              <template #default="scope">
                <span v-if="scope.row.status">
                  <ElTag
                    :type="scope.row.status === '00' ? 'success' : 'danger'"
                    size="small"
                  >
                    {{ scope.row.status }}
                  </ElTag>
                </span>
                <span v-else>-</span>
              </template>
            </ElTableColumn>

            <ElTableColumn prop="errorMessage" label="信息" min-width="150">
              <template #default="scope">
                <span
                  v-if="scope.row.errorMessage"
                  :class="getMessageClass(scope.row.requestStatus)"
                >
                  {{ scope.row.errorMessage || "-" }}
                </span>
                <span v-else>-</span>
              </template>
            </ElTableColumn>
          </ElTable>
        </div>

        <!-- 统计信息 -->
        <div class="statistics">
          <span>总记录数：{{ transactionRecords.length }}</span>
          <span>成功：{{ successCount }}</span>
          <span>失败：{{ failedCount }}</span>
          <span>进行中：{{ pendingCount }}</span>
        </div>
      </div>
    </ElDrawer>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import {
  ElButton,
  ElDrawer,
  ElTag,
  ElTable,
  ElTableColumn,
  ElMessage,
  ElMessageBox,
} from "element-plus";
import n5PosManager from "@/utils/pos/N5PosManager";
import { Http_getPayMentList } from "@/http/apiFunction";
import {
  PosConnectionStatus,
  PosHealthStatus,
  type TransactionRecord,
  type TransactionRecordEvent,
} from "@/utils/pos/N5PosManager";
import dayjs from "dayjs";

// 状态数据
const drawerVisible = ref(false);
const loading = ref(false);
const connecting = ref(false);
const disconnecting = ref(false);
// 是否显示POS机管理按钮
const showPosManagerButton = ref(false);
// 结算 loading
const settleLoading = ref(false);

// POS机状态
const serialConnectionStatus = ref<PosConnectionStatus>(
  n5PosManager.connectionStatus
);
const posHealthStatus = ref<PosHealthStatus>(n5PosManager.healthStatus);
const transactionRecords = ref<TransactionRecord[]>(
  n5PosManager.getTransactionRecords()
);

// 交易记录（按时间倒序排列，最新的在最上面）
const sortedTransactionRecords = computed(() => {
  return [...transactionRecords.value].sort(
    (a, b) => b.timestamp - a.timestamp
  );
});

// 连接状态显示
const connectionStatusText = computed(() => {
  switch (serialConnectionStatus.value) {
    case PosConnectionStatus.CONNECTED:
      return "已连接";
    case PosConnectionStatus.CONNECTING:
      return "连接中";
    case PosConnectionStatus.DISCONNECTED:
      return "未连接";
    case PosConnectionStatus.ERROR:
      return "连接错误";
    default:
      return "未知";
  }
});

const connectionStatusType = computed(() => {
  switch (serialConnectionStatus.value) {
    case PosConnectionStatus.CONNECTED:
      return "success";
    case PosConnectionStatus.CONNECTING:
      return "warning";
    case PosConnectionStatus.DISCONNECTED:
      return "info";
    case PosConnectionStatus.ERROR:
      return "danger";
    default:
      return "info";
  }
});

// 健康状态显示
const healthStatusText = computed(() => {
  switch (posHealthStatus.value) {
    case PosHealthStatus.SUCCESS:
      return "正常";
    case PosHealthStatus.FAIL:
      return "主机拒绝";
    case PosHealthStatus.ECR_BUSY:
      return "POS机繁忙";
    default:
      return "未知";
  }
});

const healthStatusType = computed(() => {
  switch (posHealthStatus.value) {
    case PosHealthStatus.SUCCESS:
      return "success";
    case PosHealthStatus.FAIL:
      return "danger";
    case PosHealthStatus.ECR_BUSY:
      return "warning";
    default:
      return "info";
  }
});

// 统计信息
const successCount = computed(() => {
  return transactionRecords.value.filter(
    (r) => r.requestStatus === "completed" && r.status === "00"
  ).length;
});

const failedCount = computed(() => {
  return transactionRecords.value.filter(
    (r) =>
      r.requestStatus === "failed" ||
      r.requestStatus === "timeout" ||
      r.requestStatus === "aborted" ||
      (r.requestStatus === "completed" && r.status !== "00")
  ).length;
});

const pendingCount = computed(() => {
  return transactionRecords.value.filter(
    (r) => r.requestStatus === "pending" || r.requestStatus === "sent"
  ).length;
});

// 监听器引用
let unsubscribeSerialStatus: (() => void) | null = null;
let unsubscribeTransactionRecord: (() => void) | null = null;

/**
 * 检查是否有POS机支付方式
 * 判断支付方式列表中是否包含POS机相关的支付类型
 */
const checkPosPaymentAvailable = async () => {
  try {
    const res = await Http_getPayMentList();
    if (res.code === 0 && res.data) {
      // POS机相关的支付类型：1020、1021、1022、1024
      const posPayTypes = [1020, 1021, 1022, 1024];

      // 检查是否存在任意一个POS支付类型
      const hasPosPayment = res.data.some((item) =>
        posPayTypes.includes(item.payType)
      );

      showPosManagerButton.value = hasPosPayment;
    }
  } catch (error) {
    console.error("获取支付方式列表失败:", error);
    // 发生错误时默认不显示按钮
    showPosManagerButton.value = false;
  }
};

// 生命周期钩子
onMounted(() => {
  // 检查是否有POS机支付方式
  checkPosPaymentAvailable();

  // 添加串口状态监听器
  unsubscribeSerialStatus = n5PosManager.addSerialStatusListener(
    ({ connectionStatus, healthStatus }) => {
      serialConnectionStatus.value = connectionStatus;
      posHealthStatus.value = healthStatus;
    }
  );

  // 添加交易记录监听器
  unsubscribeTransactionRecord = n5PosManager.addTransactionRecordListener(
    (event: TransactionRecordEvent) => {
      transactionRecords.value = [...event.records];
    }
  );

  // 初始化数据
  serialConnectionStatus.value = n5PosManager.connectionStatus;
  posHealthStatus.value = n5PosManager.healthStatus;
  transactionRecords.value = n5PosManager.getTransactionRecords();
});

onBeforeUnmount(() => {
  // 移除监听器
  if (unsubscribeSerialStatus) {
    unsubscribeSerialStatus();
  }
  if (unsubscribeTransactionRecord) {
    unsubscribeTransactionRecord();
  }
});

// 方法
const handleConnect = async () => {
  try {
    connecting.value = true;
    await n5PosManager.connect();
    ElMessage.success("POS机连接成功");
  } catch (error) {
    ElMessage.error(
      `连接失败: ${error instanceof Error ? error.message : "未知错误"}`
    );
  } finally {
    connecting.value = false;
  }
};

const handleDisconnect = async () => {
  try {
    disconnecting.value = true;
    await n5PosManager.disconnect();
    ElMessage.success("POS机已断开连接");
  } catch (error) {
    ElMessage.error(
      `断开连接失败: ${error instanceof Error ? error.message : "未知错误"}`
    );
  } finally {
    disconnecting.value = false;
  }
};

const handleClearConnection = async () => {
  try {
    await ElMessageBox.confirm(
      "清理连接将断开当前串口并清除自动连接记录，下次连接时需要重新选择串口设备。确定要继续吗？",
      "提示",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    await n5PosManager.clearSerialConnection();
    ElMessage.success("串口连接已清理，本地存储已清除");
  } catch (error) {
    if (error !== "cancel") {
      ElMessage.error(
        `清理连接失败: ${error instanceof Error ? error.message : "未知错误"}`
      );
    }
  }
};

const handleSettle = async () => {
  settleLoading.value = true;
  const resutle = await n5PosManager.settleMent();
  settleLoading.value = false;
  ElMessage({
    type: resutle.code === 0 ? "success" : "error",
    message:
      resutle.code === 0 ? "清机结算成功" : `清机结算失败,${resutle.msg}`,
  });
};

// 格式化金额
const formatAmount = (amount: number): string => {
  return `¥${amount.toFixed(2)}`;
};

// 格式化时间
const formatTime = (timestamp: number): string => {
  return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
};

// 获取请求状态类型
const getRequestStatusType = (
  status: string
): "success" | "warning" | "danger" | "info" => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
    case "sent":
      return "warning";
    case "failed":
    case "timeout":
    case "aborted":
      return "danger";
    default:
      return "info";
  }
};

// 获取请求状态文本
const getRequestStatusText = (status: string): string => {
  switch (status) {
    case "pending":
      return "等待中";
    case "sent":
      return "已发送";
    case "completed":
      return "已完成";
    case "failed":
      return "失败";
    case "timeout":
      return "超时";
    case "aborted":
      return "已取消";
    default:
      return status;
  }
};

// 获取信息文本的样式类名
const getMessageClass = (status: string): string => {
  switch (status) {
    case "completed":
      return "success-message";
    case "pending":
    case "sent":
      return "warning-message";
    case "failed":
    case "timeout":
    case "aborted":
      return "error-message";
    default:
      return "info-message";
  }
};
</script>

<style lang="scss" scoped>
.pos-manager-wrapper {
  display: inline-block;
}

.pos_manager_btn {
  margin-right: 10px;
}

.pos-manager-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 10px;
}

.status-bar {
  display: flex;
  gap: 24px;
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 16px;

  .status-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .status-label {
      font-weight: 500;
      color: #606266;
    }
  }
}

.action-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.transaction-table {
  flex: 1;
  margin-bottom: 16px;

  .txn-id {
    font-family: monospace;
    font-size: 12px;
    color: #409eff;
  }

  .success-message {
    color: #67c23a;
    font-size: 12px;
  }

  .warning-message {
    color: #e6a23c;
    font-size: 12px;
  }

  .error-message {
    color: #f56c6c;
    font-size: 12px;
  }

  .info-message {
    color: #909399;
    font-size: 12px;
  }
}

.statistics {
  display: flex;
  gap: 24px;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;

  span {
    &:not(:first-child) {
      &::before {
        content: "|";
        margin-right: 24px;
        color: #dcdfe6;
      }
    }
  }
}

:deep(.el-drawer__header) {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

:deep(.el-drawer__body) {
  padding: 20px;
}

:deep(.el-table) {
  font-size: 13px;

  .cell {
    padding: 8px 4px;
  }
}
</style>
